import type {
  ErrorResponse,
  ReleaseResponse,
} from 'services/api/responses/responseTypes';
import { Song } from 'services/store/songs/types';
import {
  ReleaseFieldType,
  type ReleaseFieldValue,
  type ReleaseSongFieldType,
  type ReleaseSongFieldValue,
} from 'services/store/distribution/types';
import { fetchAndCombineSongsData } from 'services/store/helpers/fetchAndCombineSongsData';
import { QueryReturnValue, baseApi } from '../base';
import { TagType } from '../base/baseApi';
import {
  Artist,
  IBaseSong,
  type Release,
  type ReleaseGenre,
  type ReleaseList,
  type ReleasesStats,
} from '../entities';
import {
  transformToRelease,
  transformToReleasesStatistics,
  transformToError,
  transformToExceptionCodeError,
  transformToArtistNameError,
  transformToUserSong,
  transformToArtist,
} from './transformers';
import {
  addNewReleaseToList,
  deleteReleaseFromList,
  updateArtistNameInReleases,
  createReleasesQuery,
  updateReleaseInfo,
} from './helpers';

const distributionApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getReleasesStatistics: build.query<ReleasesStats, void>({
      query: () => ({ url: 'releases/stats' }),
      providesTags: [TagType.RELEASES_STATS],
      transformResponse: transformToReleasesStatistics,
    }),
    getGenres: build.query<ReleaseGenre[], void>({
      query: () => ({ url: 'releases/genres' }),
    }),
    getRelease: build.mutation<Release, { releaseId: string }>({
      query: ({ releaseId }) => ({
        url: `/releases/${releaseId}`,
      }),
      transformResponse: transformToRelease,
    }),
    createRelease: build.mutation<
      { release: Release; songs: Record<string, Song> },
      { songId: string }
    >({
      queryFn: async ({ songId }, _queryApi, _extraOptions, baseFetch) => {
        const formData = new FormData();
        formData.append('song_id', songId);

        const response = (await baseFetch({
          method: 'POST',
          url: `/releases`,
          body: formData,
          formData: true,
        })) as QueryReturnValue<ReleaseResponse, ErrorResponse>;

        const { data, error } = response;

        if (error) {
          throw transformToExceptionCodeError(error);
        }

        const release = transformToRelease(data);
        const songsWithPeaks = await fetchAndCombineSongsData(release.songs);

        return {
          data: {
            release,
            songs: songsWithPeaks,
          },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const {
          data: { release },
        } = await queryFulfilled;
        const updateGetAllReleasesCache = distributionApi.util.updateQueryData(
          'getAllReleases',
          undefined,
          (draft) => addNewReleaseToList(draft, release)
        );
        const updateGetDraftReleasesCache =
          distributionApi.util.updateQueryData(
            'getDraftReleases',
            undefined,
            (draft) => addNewReleaseToList(draft, release)
          );
        dispatch(updateGetAllReleasesCache);
        dispatch(updateGetDraftReleasesCache);
      },
      invalidatesTags: (_, error) => (error ? [] : [TagType.RELEASES_STATS]),
    }),
    setupArtistName: build.mutation<Artist, { name: string; save?: boolean }>({
      query: ({ name, save }) => {
        const formData = new FormData();
        formData.append('artist_name', name);
        if (save) {
          formData.append('save', 'true');
        }

        return {
          method: 'POST',
          url: `/releases/artist_name`,
          body: formData,
          formData: true,
        };
      },
      async onQueryStarted({ save }, { dispatch, queryFulfilled }) {
        if (!save) {
          return;
        }

        const {
          data: { name: artistName },
        } = await queryFulfilled;

        const updateGetAllReleasesCache = distributionApi.util.updateQueryData(
          'getAllReleases',
          undefined,
          (draft) => updateArtistNameInReleases(draft, artistName)
        );
        const updateGetDraftReleasesCache =
          distributionApi.util.updateQueryData(
            'getDraftReleases',
            undefined,
            (draft) => updateArtistNameInReleases(draft, artistName)
          );

        dispatch(updateGetAllReleasesCache);
        dispatch(updateGetDraftReleasesCache);
      },
      transformResponse: transformToArtist,
      transformErrorResponse: transformToArtistNameError,
    }),
    updateRelease: build.mutation<
      Release,
      { releaseId: string; field: ReleaseFieldType; value: ReleaseFieldValue }
    >({
      query: ({ releaseId, field, value }) => {
        const formData = new FormData();
        formData.append('_method', 'PATCH');
        formData.append(field, value);

        return {
          method: 'POST',
          url: `/releases/${releaseId}`,
          body: formData,
          formData: true,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data: updatedRelease } = await queryFulfilled;
        const updateGetAllReleasesCache = distributionApi.util.updateQueryData(
          'getAllReleases',
          undefined,
          (draft) => updateReleaseInfo(draft, updatedRelease)
        );
        const updateGetDraftReleasesCache =
          distributionApi.util.updateQueryData(
            'getDraftReleases',
            undefined,
            (draft) => updateReleaseInfo(draft, updatedRelease)
          );

        dispatch(updateGetAllReleasesCache);
        dispatch(updateGetDraftReleasesCache);
      },
      transformResponse: transformToRelease,
      transformErrorResponse: transformToError,
    }),
    updateReleaseSong: build.mutation<
      Release,
      {
        releaseId: string;
        field: ReleaseSongFieldType;
        value: ReleaseSongFieldValue;
      }
    >({
      query: ({ releaseId, field, value }) => {
        const formData = new FormData();
        formData.append('_method', 'PATCH');

        if (Array.isArray(value)) {
          value.forEach((val) => {
            formData.append(`metadata[${field}][]`, val);
          });
        } else {
          formData.append(`metadata[${field}]`, value.toString());
        }

        return {
          method: 'POST',
          url: `/releases/${releaseId}`,
          body: formData,
          formData: true,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data: updatedRelease } = await queryFulfilled;
        const updateGetAllReleasesCache = distributionApi.util.updateQueryData(
          'getAllReleases',
          undefined,
          (draft) => updateReleaseInfo(draft, updatedRelease)
        );
        const updateGetDraftReleasesCache =
          distributionApi.util.updateQueryData(
            'getDraftReleases',
            undefined,
            (draft) => updateReleaseInfo(draft, updatedRelease)
          );

        dispatch(updateGetAllReleasesCache);
        dispatch(updateGetDraftReleasesCache);
      },
      transformResponse: transformToRelease,
      transformErrorResponse: transformToError,
    }),
    submitRelease: build.mutation<Release, { releaseId: string }>({
      query: ({ releaseId }) => ({
        method: 'POST',
        url: `/releases/${releaseId}/submit`,
      }),
      async onQueryStarted({ releaseId }, { dispatch, queryFulfilled }) {
        const { data: release } = await queryFulfilled;
        const updateGetAllReleasesCache = distributionApi.util.updateQueryData(
          'getAllReleases',
          undefined,
          (draft) => updateReleaseInfo(draft, release)
        );
        const updateGetPendingReleasesCache =
          distributionApi.util.updateQueryData(
            'getPendingReleases',
            undefined,
            (draft) => addNewReleaseToList(draft, release)
          );
        const updateGetDraftReleasesCache =
          distributionApi.util.updateQueryData(
            'getDraftReleases',
            undefined,
            (draft) => deleteReleaseFromList(draft, releaseId)
          );
        dispatch(updateGetAllReleasesCache);
        dispatch(updateGetPendingReleasesCache);
        dispatch(updateGetDraftReleasesCache);
      },
      transformResponse: transformToRelease,
      transformErrorResponse: transformToExceptionCodeError,
      invalidatesTags: (_, error) => (error ? [] : [TagType.RELEASES_STATS]),
    }),
    deleteRelease: build.mutation<void, { releaseId: string }>({
      query: ({ releaseId }) => ({
        method: 'DELETE',
        url: `/releases/${releaseId}`,
      }),
      async onQueryStarted({ releaseId }, { dispatch }) {
        const updateGetAllReleasesCache = distributionApi.util.updateQueryData(
          'getAllReleases',
          undefined,
          (draft) => deleteReleaseFromList(draft, releaseId)
        );
        const updateGetDraftReleasesCache =
          distributionApi.util.updateQueryData(
            'getDraftReleases',
            undefined,
            (draft) => deleteReleaseFromList(draft, releaseId)
          );
        dispatch(updateGetAllReleasesCache);
        dispatch(updateGetDraftReleasesCache);
      },
      invalidatesTags: (_, error) => (error ? [] : [TagType.RELEASES_STATS]),
    }),
    uploadAudio: build.mutation<IBaseSong, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('music_file', file);

        return {
          method: 'POST',
          url: `/user-songs`,
          body: formData,
          formData: true,
        };
      },
      transformResponse: transformToUserSong,
      transformErrorResponse: transformToError,
    }),
    getAllReleases: build.query<ReleaseList, number | void>(
      createReleasesQuery()
    ),
    getDraftReleases: build.query<ReleaseList, number | void>(
      createReleasesQuery('DRAFT')
    ),
    getPendingReleases: build.query<ReleaseList, number | void>(
      createReleasesQuery('PENDING')
    ),
    getReleasedReleases: build.query<ReleaseList, number | void>(
      createReleasesQuery('RELEASED')
    ),
  }),
});

export default distributionApi;
